import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Square, Video, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

export const BOT_ACTIVE = ["bot_scheduled", "bot_joining", "bot_recording", "bot_processing"];
export const PLATFORM = { zoom: "Zoom", google_meet: "Google Meet", teams: "Microsoft Teams" };

const HINT = {
  bot_scheduled: ["Bot akan menyertai pada masa dijadualkan.", "The bot will join at the scheduled time."],
  bot_joining: ["Bot sedang menyertai — terima \"MinitAI Pencatat\" dari bilik menunggu jika diminta.", "Bot is joining — admit \"MinitAI Pencatat\" from the waiting room if asked."],
  bot_recording: ["Bot sedang merakam. Tamatkan mesyuarat atau tekan Hentikan bot.", "Bot is recording. End the meeting or press Stop bot."],
  bot_processing: ["Mesyuarat tamat. Rakaman sedang diambil dan akan diproses secara automatik.", "Meeting ended. The recording is being fetched and will be processed automatically."],
};

export const BotCard = ({ meeting: m, canWrite, onChange, className = "", showLink = false }) => {
  const { L } = useApp();
  const [busy, setBusy] = useState(false);
  const scheduled = m.status === "bot_scheduled";
  const stop = async () => {
    setBusy(true);
    try {
      await api.post(`/bots/${m.id}/stop`);
      toast.success(scheduled ? L("Bot dibatalkan", "Bot cancelled") : L("Bot meninggalkan mesyuarat", "Bot left the meeting"));
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const hint = HINT[m.status];
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className}`} data-testid={`bot-card-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600"><Video size={18} /></div>
          <div className="min-w-0">
            {showLink ? <Link to={`/app/meetings/${m.id}`} className="truncate font-semibold hover:underline" data-testid="bot-card-title">{m.title}</Link> : <p className="font-semibold">{PLATFORM[m.bot.platform]}</p>}
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              {showLink && <span>{PLATFORM[m.bot.platform]}</span>}
              {m.bot.join_at && <span className="flex items-center gap-1"><CalendarClock size={12} /> {new Date(m.bot.join_at).toLocaleString("ms-MY", { dateStyle: "medium", timeStyle: "short" })}</span>}
              <a href={m.bot.meeting_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-violet-700"><ExternalLink size={12} /> {L("Pautan", "Link")}</a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={m.status} />
          {canWrite && BOT_ACTIVE.includes(m.status) && m.status !== "bot_processing" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={stop} className="rounded-xl" data-testid={`bot-stop-btn-${m.id}`}>
              <Square size={12} className="mr-1" /> {scheduled ? L("Batal", "Cancel") : L("Hentikan bot", "Stop bot")}
            </Button>
          )}
        </div>
      </div>
      {hint && <p className="mt-3 text-xs text-slate-500" data-testid="bot-card-hint">{L(...hint)}</p>}
      {m.status === "failed" && m.error && <p className="mt-3 text-xs font-medium text-red-600" data-testid="bot-card-error">{m.error}</p>}
    </div>
  );
};
